import { useMutation } from '@apollo/client';
import jwt from 'jsonwebtoken';
import { useState } from 'react';
import { INVITE_CLINICIAN } from '../graphql/mutation/inviteClinician';
import { INVITE_CLINIC_ADMIN } from '../graphql/mutation/inviteClinicAdmin';
import {
  ACTIVATE_AND_UPDATE_USER,
  FORGOT_PASSWORD,
  LOGIN,
  RESET_PASSWORD,
} from '../graphql/mutation/users';
import { useStore } from '../store/restash';
import type {
  AuthTokens,
  IClinician,
  IUser,
  IUserActivate,
  IUserInvite,
  IUserLogin,
  IUserReset,
  IUserToken,
  OkResult,
  UserType,
} from '../types';

const messageFromErrorResponseBody = (err: any) => err?.networkError?.result?.errors?.[0]?.message;

const getErrorMessage = (err: any) =>
  messageFromErrorResponseBody(err) ||
  err?.message ||
  err?.graphQLErrors?.[0]?.message ||
  err?.networkError?.message ||
  'An unknown error occurred';

const INCORRECT_CREDENTIALS_MESSAGE = 'Incorrect email or password';

export class IncorrectCredentialsError extends Error {
  constructor() {
    super(INCORRECT_CREDENTIALS_MESSAGE);
    this.name = 'IncorrectCredentialsError';
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

const toLoginError = (err: any) =>
  getErrorMessage(err) === INCORRECT_CREDENTIALS_MESSAGE
    ? new IncorrectCredentialsError()
    : new Error(getErrorMessage(err));

const useAuth = () => {
  const [loginMutation] = useMutation<{ login: AuthTokens }>(LOGIN);
  const [forgotPasswordMutation] = useMutation<{ forgotPassword: OkResult }>(FORGOT_PASSWORD);
  const [resetPasswordMutation] = useMutation<{ resetPassword: IUser }>(RESET_PASSWORD);
  const [activateUserMutation] = useMutation<{ activateAndUpdateUser: IUser }>(
    ACTIVATE_AND_UPDATE_USER
  );
  const [inviteClinicianMutation] = useMutation<{ inviteClinician: IClinician }>(INVITE_CLINICIAN);
  const [inviteClinicAdminMutation] = useMutation<{ inviteClinicAdmin: IUser }>(
    INVITE_CLINIC_ADMIN
  );

  const [store, dispatch] = useStore();
  const [isLoading, setIsLoading] = useState(false);

  const { isAuthenticated, user, token } = store;

  const decode = (tkn: string): IUserToken => jwt.decode(tkn) as IUserToken;

  const login = async (creds: IUserLogin) => {
    try {
      setIsLoading(true);

      const { data } = await loginMutation({
        variables: { email: creds.user, password: creds.pass },
      });

      if (!data) throw new Error('data not found');

      const decodedToken = decode(data.login.accessToken);

      dispatch({
        isAuthenticated: true,
        user: decodedToken,
        token: data.login.accessToken,
        refreshToken: data.login.refreshToken,
      });
    } catch (err: any) {
      throw toLoginError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    dispatch({
      isAuthenticated: false,
      user: null,
      token: null,
      refreshToken: null,
    });
  };

  const reset = async (creds: IUserReset) => {
    try {
      setIsLoading(true);

      const { data } = await resetPasswordMutation({
        variables: { token: creds.token as string, password: creds.pass },
      });

      if (!data) throw new Error('data not found');
    } catch (err: any) {
      throw new Error(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const activate = async (credentials: IUserActivate) => {
    try {
      setIsLoading(true);

      const { data } = await activateUserMutation({
        variables: {
          activationToken: credentials.token,
          password: credentials.pass,
          nameFirst: credentials.nameFirst,
          nameLast: credentials.nameLast,
        },
      });

      if (!data) throw new Error('data not found');
    } catch (err: any) {
      throw new Error(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const forgot = async (credentials: { email: string }) => {
    try {
      setIsLoading(true);

      const { data } = await forgotPasswordMutation({ variables: { email: credentials.email } });

      if (!data) throw new Error('data not found');
    } catch (err: any) {
      throw new Error(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const inviteClinician = async (
    user: Pick<IUserInvite, 'email' | 'clinicId' | 'firstName' | 'lastName'>
  ) => {
    try {
      setIsLoading(true);

      const { data } = await inviteClinicianMutation({
        variables: {
          input: {
            email: user.email,
            clinicId: user.clinicId,
            nameFirst: user.firstName,
            nameLast: user.lastName,
          },
        },
      });

      if (!data) throw new Error('data not found');
    } catch (err: any) {
      throw new Error(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const inviteAdmin = async (
    user: Pick<IUserInvite, 'email' | 'clinicId' | 'firstName' | 'lastName' | 'role'>
  ) => {
    try {
      setIsLoading(true);

      // Role 2 = Super Admin, Role 3 = Regular Admin
      const roleName = user.role === 2 ? 'clinicsuperadmin' : 'clinicadmin';

      const { data } = await inviteClinicAdminMutation({
        variables: {
          input: {
            email: user.email,
            clinicId: user.clinicId,
            nameFirst: user.firstName,
            nameLast: user.lastName,
            roleName,
          },
        },
      });

      if (!data) throw new Error('data not found');
    } catch (err: any) {
      throw new Error(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const hasPermission = (permission: string): boolean => {
    if (!user?.permissions) return false;
    return user.permissions.includes(permission);
  };

  const isType = (type: UserType): boolean => {
    if (!user) return false;
    return user.userType === type;
  };

  return {
    decode,
    isAuthenticated,
    isLoading,
    isType,
    user,
    token,
    login,
    logout,
    forgot,
    reset,
    activate,
    inviteClinician,
    inviteAdmin,
    hasPermission,
  };
};

export default useAuth;
